import { useCallback } from "react";
import { toast } from "sonner";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys.jsx";
import { useConfirm } from "@omit/react-confirm-dialog";
import { EntityRecord, WidgetParamKeys } from "@/types/entity";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useBoardUsers } from "@/react/hooks/useBoardUsers";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import {
  createChecklist,
  isDraftId,
  type Checklist
} from "@/react/components/entityViews/checklist/types";
import {
  useChecklistsQuery,
  usePatchChecklistMutation,
  useDeleteChecklistMutation,
  useDuplicateChecklistMutation,
  useImportChecklistItemsMutation,
  useExportChecklistItemsMutation
} from "@/react/components/entityViews/checklist/api/checklist.api";
import { checklistEntityPathType } from "@/react/components/entityViews/checklist/api/checklist.query-keys";

export type TUseSwivuChecklistReturn = ReturnType<typeof useSwivuChecklist>;

function readBoardId(selectedBoard: unknown): number | null {
  const board = selectedBoard as { id?: number; record?: { id?: number } } | null;
  return board?.id ?? board?.record?.id ?? null;
}

function savedGrant(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/** Same fallback as Settings → Default Permissions and the checklist create API. */
function defaultChecklistGrants(
  userPreferences: { getSetting: (key: string) => string | undefined } | null | undefined,
  ownGrant: number | undefined
): { canEdit?: number; canView?: number } {
  return {
    canEdit: savedGrant(userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.userDefaultEditPermission)) ?? ownGrant,
    canView: savedGrant(userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.userDefaultViewPermission)) ?? ownGrant
  };
}

export const useSwivuChecklist = ({ record }: { record: EntityRecord | null }) => {
  const context = useSwivuBryntumContext();
  const { user, entityManager, selectedBoard, project, userPreferences } = context;
  const { myBoardUserId } = useBoardUsers();
  const { copyEntityLink } = entityManager;
  const confirm = useConfirm();
  const boardId = readBoardId(selectedBoard);
  const pathType = checklistEntityPathType(record?.entityType);
  const entityId = record?.isPhantom ? null : record?.id;

  const {
    checklists,
    totalCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingChecklists,
    isFetching: isFetchingChecklists,
    refetch
  } = useChecklistsQuery({
    boardId,
    entityType: pathType,
    entityId,
    enabled: Boolean(boardId && pathType && entityId != null)
  });

  const patchChecklistMutation = usePatchChecklistMutation();
  const deleteChecklistMutation = useDeleteChecklistMutation();
  const duplicateChecklistMutation = useDuplicateChecklistMutation();
  const importChecklistItemsMutation = useImportChecklistItemsMutation();
  const exportChecklistItemsMutation = useExportChecklistItemsMutation();

  const refreshChecklists = useCallback(async () => {
    if (!boardId || !pathType || entityId == null) return [] as Checklist[];
    return refetch();
  }, [boardId, pathType, entityId, refetch]);

  const canEditChecklist = (checklist: Checklist) => {
    // List rows may omit grants; user.can(0) throws.
    if (!user || checklist.canEdit == null || !checklist.canEdit) return false;
    return user.can(checklist.canEdit);
  };

  const isUserAssignedToChecklist = (checklist: Checklist) => {
    if (!user) return false;
    const mine = myBoardUserId();
    return mine != null && (checklist.assignedBoardUsers ?? []).includes(mine);
  };

  const canViewChecklist = (checklist: Checklist) => {
    if (!user) return false;
    if (isUserAssignedToChecklist(checklist)) return true;
    if (checklist.canView == null || !checklist.canView) return false;
    return user.can(checklist.canView);
  };

  const createChecklistRecord = useCallback(
    (title: string = "", assignedBoardUsers: number[] = []) => {
      if (!record || record.isPhantom || !pathType) return null;

      const entityKey =
        pathType === "task" ? "taskId" : pathType === "resource" ? "resourceId" : "clientId";

      const boardUser = project?.boardUsersStore?.find((member) => member.userId === user?.id) as
        | { grants?: { value?: number }[] }
        | undefined;
      const ownGrantValues = (boardUser?.grants ?? [])
        .map((grant) => Number(grant.value))
        .filter((value) => Number.isFinite(value) && value > 0);
      const ownGrant = ownGrantValues.length ? Math.min(...ownGrantValues) : undefined;
      const { canEdit, canView } = defaultChecklistGrants(userPreferences, ownGrant);

      return createChecklist({
        title,
        [entityKey]: record.id,
        archived: record.archived,
        canEdit,
        canView,
        order: 0,
        assignedBoardUsers,
        recurrence: "none"
      });
    },
    [record, pathType, project, user?.id, userPreferences]
  );

  const updateChecklist = useCallback(
    (checklist: Checklist, updates: Partial<Checklist>) => {
      if (!isDraftId(checklist.id)) {
        if (checklist.canEdit == null || !user?.can(checklist.canEdit)) {
          toast.error("Permission Denied", {
            description: "You don't have permission to edit this checklist"
          });
          return false;
        }
      }

      Object.assign(checklist, updates);
      return true;
    },
    [user]
  );

  const deleteChecklist = useCallback(
    async (checklist: Checklist, onSuccess?: () => void): Promise<boolean> => {
      if (checklist.canEdit == null || !user?.can(checklist.canEdit)) {
        toast.error("Permission Denied", {
          description: "You don't have permission to delete this checklist"
        });
        return false;
      }

      const isDelete = await confirm({
        title: `Delete checklist "${checklist.title}"`,
        description: `Are you sure you want to delete the checklist "${checklist.title}"?`,
        confirmText: "Delete",
        cancelButton: {
          variant: "ghost"
        },
        confirmButton: {
          className: "dark:bg-red-900 dark:text-red-200 bg-red-100 text-red-800 hover:dark:bg-red-950 hover:dark:text-red-300 hover:bg-red-200 transition-colors"
        }
      });

      if (isDelete) {
        if (boardId && checklist.id != null && !isDraftId(checklist.id)) {
          try {
            await deleteChecklistMutation.mutateAsync({
              boardId,
              checklistId: checklist.id,
              entityType: pathType,
              entityId
            });
          } catch {
            toast.error("Unable to delete checklist.");
            return false;
          }
        }
        onSuccess?.();
        toast.success("Checklist Deleted", {
          description: `"${checklist.title}" has been successfully deleted`
        });
      }
      return isDelete;
    },
    [user, confirm, boardId, pathType, entityId, deleteChecklistMutation]
  );

  const toggleArchiveChecklist = useCallback(
    (checklist: Checklist): boolean => {
      if (checklist.canEdit == null || !user?.can(checklist.canEdit)) {
        toast.error("Permission Denied", {
          description: "You don't have permission to archive this checklist"
        });
        return false;
      }

      if (!boardId || checklist.id == null || isDraftId(checklist.id)) return false;

      const wasArchived = checklist.archived;
      void patchChecklistMutation
        .mutateAsync({
          boardId,
          checklistId: checklist.id,
          body: { archived: !wasArchived },
          entityType: pathType,
          entityId
        })
        .catch(() => toast.error("Unable to update archive state."));

      if (wasArchived) {
        toast.success("Checklist Unarchived", {
          description: `"${checklist.title}" has been unarchived`
        });
      } else {
        toast.success("Checklist Archived", {
          description: `"${checklist.title}" has been archived`
        });
      }

      return true;
    },
    [user, boardId, pathType, entityId, patchChecklistMutation]
  );

  const duplicateChecklistRecord = useCallback(
    async (checklist: Checklist) => {
      if (checklist.canEdit == null || !user?.can(checklist.canEdit)) {
        toast.error("Permission Denied", {
          description: "You don't have permission to duplicate this checklist"
        });
        return false;
      }
      if (!boardId || checklist.id == null || !pathType) {
        toast.error("Unable to duplicate this checklist.");
        return false;
      }

      if (!record || record.id == null || record.isPhantom) {
        toast.error("Unable to duplicate this checklist.");
        return false;
      }

      try {
        await duplicateChecklistMutation.mutateAsync({
          boardId,
          sourceChecklistId: checklist.id,
          createBody: {
            title: `Copy of ${checklist.title}`,
            [pathType]: record.id,
            archived: checklist.archived,
            can_edit_grant: checklist.canEdit,
            can_view_grant: checklist.canView,
            recurrence: checklist.recurrence ?? "none"
          },
          duplicateBody: {
            [pathType]: record.id,
            archived: checklist.archived,
            canEdit: checklist.canEdit,
            canView: checklist.canView,
            duplicate_id: checklist.id
          },
          entityType: pathType,
          entityId: record.id
        });
        toast.success("Checklist Duplicated", {
          description: `"${checklist.title}" has been successfully duplicated`
        });
        return true;
      } catch {
        toast.error("Duplication Failed", {
          description: `Failed to duplicate "${checklist.title}"`
        });
        return false;
      }
    },
    [user, boardId, record, pathType, duplicateChecklistMutation]
  );

  const importChecklistItems = useCallback(
    async (checklist: Checklist, file: File) => {
      if (checklist.canEdit == null || !user?.can(checklist.canEdit)) {
        toast.error("Permission Denied", {
          description: "You don't have permission to import items to this checklist"
        });
        return false;
      }
      if (!file || file.type !== "text/plain") {
        toast.error("Unsupported File Type", {
          description: "Please upload files with extensions: .txt"
        });
        return false;
      }
      if (!boardId || checklist.id == null || isDraftId(checklist.id)) {
        toast.error("Import Failed", {
          description: `Failed to import items to "${checklist.title}"`
        });
        return false;
      }

      try {
        const checklistItemsData = await file.text();
        await importChecklistItemsMutation.mutateAsync({
          boardId,
          checklistId: checklist.id,
          checklistItemsData,
          entityType: pathType,
          entityId
        });
        toast.success("Import Successful", {
          description: `Items imported successfully to "${checklist.title}"`
        });
        return true;
      } catch {
        toast.error("Import Failed", {
          description: `Failed to import items to "${checklist.title}"`
        });
        return false;
      }
    },
    [user, boardId, pathType, entityId, importChecklistItemsMutation]
  );

  const exportChecklistItems = useCallback(
    async (checklist: Checklist) => {
      if (checklist.id == null || isDraftId(checklist.id)) {
        toast.error("Export Failed", {
          description: `Failed to export items from "${checklist.title}"`
        });
        return false;
      }
      try {
        await exportChecklistItemsMutation.mutateAsync({
          checklistId: checklist.id,
          fileName: `${checklist.title}`
        });
        toast.success("Export Successful", {
          description: `Items from "${checklist.title}" exported successfully`
        });
        return true;
      } catch {
        toast.error("Export Failed", {
          description: `Failed to export items from "${checklist.title}"`
        });
        return false;
      }
    },
    [exportChecklistItemsMutation]
  );

  const copyChecklistLink = useCallback(
    async (checklist: Checklist) => {
      if (!copyEntityLink) {
        console.error("copyEntityLink is not available");
        toast.error("Unable to copy link");
        return;
      }
      const entityId = checklist.taskId ?? checklist.resourceId ?? checklist.clientId ?? record?.id;
      if (entityId == null) {
        toast.error("Unable to copy link");
        return;
      }
      const entityType = checklist.taskId
        ? ENTITY_TYPES.TASK
        : checklist.resourceId
          ? ENTITY_TYPES.RESOURCE
          : checklist.clientId
            ? ENTITY_TYPES.CLIENT
            : record?.entityType ?? ENTITY_TYPES.TASK;
      await copyEntityLink(WidgetParamKeys.CHECKLIST.EDIT, checklist.id, `Checklist link copied successfully!`, undefined, {
        entityType,
        entityId
      });
    },
    [copyEntityLink, record]
  );

  return {
    checklists,
    totalCount,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoadingChecklists,
    isFetchingChecklists,
    createChecklist: createChecklistRecord,
    updateChecklist,
    deleteChecklist,
    toggleArchiveChecklist,
    duplicateChecklistRecord,
    importChecklistItems,
    exportChecklistItems,
    canEditChecklist,
    copyChecklistLink,
    refreshChecklists,
    canViewChecklist,
    isUserAssignedToChecklist
  };
};
